// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — one round of a live race, decoupled from Trystero, the DOM and the clock (driven
// by step()/finish checks), which is what makes the host-transfer contract testable
// without a network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer catches on its OWN copy of the byte-identical seeded
// firefly schedule and reaches its OWN game-over (the clock) with no host help. NO game
// state ever crosses the wire — only a tiny status gossip (score, catches, best combo, and
// on finishing: done). HOST TRANSFER IS A PURE DISPLAY CONCERN; onHostChange is wired
// regardless.

import { createGame, drainEvents, step as stepGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  catches: number;
  bestCombo: number;
  flies: number; // the knowable perfect for this board
  finished: boolean;
  left: boolean;
}

/** Tiny wire status. */
export interface RaceMsg {
  s?: number; // score
  c?: number; // catches
  cb?: number; // best combo
  d?: boolean; // done
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a
 *  window; further pokes while pending do not push it back. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    const flies = this.state.flies;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        score: 0,
        catches: 0,
        bestCombo: 0,
        flies,
        finished: false,
        left: false,
      });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  /** Advance the local run by `dtMs` under the current per-zone energy. Spectators no-op. */
  step(energy: readonly number[], dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    const scoreBefore = this.state.score;
    const catchesBefore = this.state.catches;
    stepGame(this.state, energy, dtMs);
    const events = drainEvents(this.state);

    const me = this.self();
    if (me) {
      me.score = this.state.score;
      me.catches = this.state.catches;
      me.bestCombo = this.state.bestCombo;
    }
    if (this.state.score !== scoreBefore || this.state.catches !== catchesBefore) {
      this.deps.send({ s: this.state.score, c: this.state.catches, cb: this.state.bestCombo });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.finished = true;
      me.score = this.state.score;
      me.catches = this.state.catches;
      me.bestCombo = this.state.bestCombo;
    }
    this.deps.send({ d: true, s: this.state.score, c: this.state.catches, cb: this.state.bestCombo });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return; // never let the wire overwrite our own run
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.s === 'number') r.score = Math.max(0, msg.s | 0);
    if (typeof msg.c === 'number') r.catches = Math.max(0, msg.c | 0);
    if (typeof msg.cb === 'number') r.bestCombo = Math.max(0, msg.cb | 0);
    if (msg.d) r.finished = true;
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.finished) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.finished && !r.left) return false;
    return true;
  }

  /** Highest score first; departed peers last; ties broken by best combo then seat. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      if (b.score !== a.score) return b.score - a.score;
      if (b.bestCombo !== a.bestCombo) return b.bestCombo - a.bestCombo;
      return a.seat - b.seat;
    });
  }
}
